import { OrderStatus, Track } from "@/types";
import cn from "@/utils/class-names";
import { useState, useEffect, useContext } from "react";
import { Badge, Text } from "rizzui";
import { createOrderTrack, fetchTracks } from "@/server/api-services";
import toast from "react-hot-toast";
import { HeaderCell } from "@/components/legacy-table";
import WidgetCard from "@/components/cards/widget-card";
import ControlledTable from "@/components/controlled-table";
import { formatDate } from "@/utils/format-date";
import { AddTrackModal } from "./add-Modal";
import { AuthContext } from "@/auth/authContext";
import { permissions } from "../../roles-permissions/permissions";

export const getColumns = () => [
    {
        title: <span className="ms-6 block whitespace-nowrap">Date</span>,
        dataIndex: "createdDate",
        key: "createdDate",
        width: 200,
        render: (createdDate: Date) => (
            <span className="ms-6 block">
                <Text className="mb-1 font-medium text-gray-700">
                    {formatDate(createdDate, "MMMM D, YYYY")}
                </Text>
                <Text className="text-[13px] text-gray-500">
                    {formatDate(createdDate, "h:mm A")}
                </Text>
            </span>
        ),
    },
    {
        title: <HeaderCell title="Location" />,
        dataIndex: "location",
        key: "location",
        width: 200,
        render: (location: string) => <Text>{location}</Text>,
    },
    {
        title: <HeaderCell title="Status" />,
        dataIndex: "status",
        key: "status",
        width: 200,
        render: (status: string) =>
            status === OrderStatus.Delivered ? (
                <Badge color="success" variant="flat" rounded="md">
                    {status}
                </Badge>
            ) : status === OrderStatus.Requested ? (
                <Badge color="warning" variant="flat" rounded="md">
                    {status}
                </Badge>
            ) : status === OrderStatus.Cancelled ? (
                <Badge color="danger" variant="flat" rounded="md">
                    {status}
                </Badge>
            ) : status === OrderStatus.Processing ? (
                <Badge color="secondary" variant="flat" rounded="md">
                    {status}
                </Badge>
            ) : status === OrderStatus.Purchased ? (
                <Badge color="primary" variant="flat" rounded="md">
                    {status}
                </Badge>
            ) : status === OrderStatus.InTransit ? (
                <Badge color="info" variant="flat" rounded="md">
                    {status}
                </Badge>
            ) : (
                <></>
            ),
    },
    {
        title: <HeaderCell title="CreatedBy" />,
        dataIndex: "createdBy",
        key: "createdBy",
        width: 200,
        render: (createdBy: string) => <Text>{createdBy}</Text>,
    },
    {
        title: <HeaderCell title="Remarks" />,
        dataIndex: "remarks",
        key: "remarks",
        width: 200,
        render: (remarks: string) => <Text>{remarks}</Text>,
    },
];

interface TrackingHistoryTableProps {
    orderId: string;
}

export default function TrackingHistoryTable({
    orderId,
}: TrackingHistoryTableProps) {
    const [trackingHistory, setTrackingHistory] = useState([] as Track[]);

    const { role } = useContext(AuthContext);

    useEffect(() => {
        if (orderId !== undefined) {
            getTracks();
        }
    }, [orderId]);

    async function getTracks() {
        const response = await fetchTracks(orderId);
        if (response.statusCode !== 200) {
            toast.error("Failed to fetch order");
            return;
        }

        var data = response.data as Track[];

        setTrackingHistory(data);
    }

    async function addTrack(track: Track) {
        const response = await createOrderTrack(orderId, track);
        if (response.statusCode !== 200) {
            toast.error("Failed to add track");
            return;
        }
        getTracks();
    }

    return (
        <div className={cn("w-full")}>
            <WidgetCard
                title="Tracking History"
                className={cn("flex flex-col")}
                headerClassName="widget-card-header flex-col sm:flex-row"
                action={
                    role &&
                    (role.includes(permissions.transactions.addTransaction) ||
                        role.includes(permissions.system.admin)) && (
                        <AddTrackModal
                            show={true}
                            handleSave={addTrack}
                            title="Add Tracking"
                            description="Add new track to the order"
                        />
                    )
                }
            >
                <div className={cn("table-wrapper flex-grow-mx-5 lg:-mx-7")}>
                    {trackingHistory ? (
                        <ControlledTable
                            data={trackingHistory}
                            columns={getColumns()}
                            scroll={{ x: "100%", y: 240 }}
                            sticky={true}
                            variant="modern"
                            className="mt-4"
                        />
                    ) : (
                        <p className="text-center">No tracking data found</p>
                    )}
                </div>
            </WidgetCard>
        </div>
    );
}
